/**
 * svgDividers.js — pure SVG divider-style library (DL-042, bucket-D wave D1).
 *
 * Ports the 37-style / 7-family divider set from the design deck
 *   design-handoff/concepts/divider-styles-proposal/index.html (v3) +
 *   the gradient-FIXED baked port design-handoff/harness/elevations/
 *   ewflow-picker-text.elevation.mjs (DV IIFE)
 * into the real Frontend as a framework-free string builder. Every geometry
 * is generated in-code (no traced/stock assets); the SVG is crisp at any
 * width and lightweight (SVG/CSS over raster — lightweight-without-losing-
 * aesthetics). The `draw` fns are copied VERBATIM from the deck so the live
 * block matches the reviewed design exactly.
 *
 * This module is PURE (no React, no DOM) so it is unit-tested directly.
 * Consumers: Shared/DividerStyles (family-grid picker) + CodeBlocks/Divider
 * (block render), both behind DIVIDER_SVG_STYLES_V3_ENABLED.
 *
 * GRADIENT BUG FIX carried over from the deck: onePaint()/fade gradients use
 * gradientUnits="userSpaceOnUse" with an explicit x-span (x1=0..x2=W). The SVG
 * default (objectBoundingBox) degenerates on a zero-height horizontal line so a
 * gradient would drop the divider entirely; user-space painting keeps it visible.
 */

export const DV_W = 560;
const W = DV_W;
const f = (n) => Math.round(n * 10) / 10;

/**
 * Allow only real CSS color literals into the generated SVG string. Rejects
 * anything containing quotes / angle brackets / `url(` etc. that could break out
 * of the fill/stroke attribute (defence-in-depth XSS guard — color originates
 * from the block's color picker so it is normally a hex, but never trust it).
 * Returns the color if safe, else null.
 */
export function sanitizeColor(c) {
  if (typeof c !== "string") return null;
  const v = c.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
  if (/^(rgb|rgba|hsl|hsla)\(\s*[0-9.,%\s/]+\)$/i.test(v)) return v;
  if (/^[a-zA-Z]{1,24}$/.test(v)) return v; // named color, e.g. "black"
  return null;
}

function wrap(h, inner, zoom) {
  // `zoom` center-crops the viewBox so a picker THUMBNAIL renders the pattern
  // large & legible (fewer repeats, each bigger). Center-crop (not left-crop) so
  // center-motif styles (laurel/scroll/fleuron) keep their motif. Block render
  // passes no zoom -> full 560-wide divider.
  let vb = `0 0 ${W} ${h}`;
  if (zoom && zoom > 1) {
    const cw = W / zoom;
    const x0 = (W - cw) / 2;
    vb = `${f(x0)} 0 ${f(cw)} ${h}`;
  }
  return `<svg class="dvsvg" viewBox="${vb}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="divider preview">${inner}</svg>`;
}

/* smooth sine path (Q half-waves). ctrl = control-offset (peak ~ ctrl/2). */
function sineP(mid, ctrl, L, x0, x1) {
  const half = L / 2;
  let x = x0,
    s = 1,
    d = `M ${f(x)} ${f(mid)}`;
  while (x < x1 - 0.01) {
    const nx = Math.min(x + half, x1);
    d += ` Q ${f(x + (nx - x) / 2)} ${f(mid - s * ctrl)}, ${f(nx)} ${f(mid)}`;
    s = -s;
    x = nx;
  }
  return d;
}

function helixInner(pA, pB, t, mid, C, L, x0, x1) {
  const a = `<path d="${sineP(mid, C, L, x0, x1)}" fill="none" stroke="${pA}" stroke-width="${t}" stroke-linecap="round"/>`;
  const b = `<path d="${sineP(mid, -C, L, x0, x1)}" fill="none" stroke="${pB}" stroke-width="${t}" stroke-linecap="round"/>`;
  let rungs = "";
  const peak = C / 2;
  for (let x = x0 + L / 4; x < x1; x += L / 2) {
    rungs += `<line x1="${f(x)}" y1="${f(mid - peak)}" x2="${f(x)}" y2="${f(mid + peak)}" stroke="${pA}" stroke-width="1.1" stroke-opacity="0.32"/>`;
  }
  return rungs + a + b;
}

/* ==== the expanded set: 33 styles / 6 families (draw fns verbatim from the deck) ==== */
const STYLES = [
  /* Family 1 · Lines, dots & beads */
  { fam: "dots", id: "dotted", name: "Dotted (round beads)", t: 3, h: 16, anim: { p: 18, dur: 6 },
    draw(p, t, o) { const mid = 8, r = Math.max(t * 1.5, 4.6); let s = "";
      for (let x = o.x0; x <= o.x1; x += o.p) { s += `<circle cx="${f(x)}" cy="${mid}" r="${f(r)}" fill="${p}"/>`; } return s; } },
  { fam: "dots", id: "graduated-dots", name: "Graduated dots", t: 3, h: 16,
    draw(p, t) { const mid = 8, rmax = Math.max(t * 1.7, 5), rmin = 1.8, n = 19, gg = (W - 20) / (n - 1); let o = "";
      for (let i = 0; i < n; i++) { const r = rmin + (rmax - rmin) * Math.sin(Math.PI * i / (n - 1)); o += `<circle cx="${f(10 + i * gg)}" cy="${mid}" r="${f(r)}" fill="${p}"/>`; } return o; } },
  { fam: "dots", id: "dot-dash", name: "Dot-dash (Morse)", t: 3, h: 12, anim: { p: 37, dur: 7 },
    draw(p, t, o) { const mid = 6, r = Math.max(t / 1.3, 2.4), dash = 16; let s = "";
      for (let x = o.x0; x < o.x1; x += o.p) { s += `<rect x="${f(x)}" y="${f(mid - t / 2)}" width="${dash}" height="${t}" rx="${f(t / 2)}" fill="${p}"/>`;
        s += `<circle cx="${f(x + dash + 8 + r)}" cy="${mid}" r="${f(r)}" fill="${p}"/>`; } return s; } },
  { fam: "dots", id: "beaded", name: "Beaded rule", t: 1.8, h: 14, anim: { p: 56, dur: 9 },
    draw(p, t, o) { const mid = 7, br = 3.4; let s = `<line x1="${f(o.x0)}" y1="${mid}" x2="${f(o.x1)}" y2="${mid}" stroke="${p}" stroke-width="${t}"/>`;
      for (let x = o.x0 + o.p / 2; x < o.x1; x += o.p) s += `<circle cx="${f(x)}" cy="${mid}" r="${br}" fill="${p}"/>`; return s; } },
  /* Family 2 · Waves & curves */
  { fam: "waves", id: "wavy", name: "Wavy (sine)", t: 3, h: 30, asym: true, anim: { p: 56, dur: 7 },
    draw(p, t, o) { return `<path d="${sineP(15, 12, 56, o.x0, o.x1)}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linecap="round"/>`; } },
  { fam: "waves", id: "scallop", name: "Scallop (arcs)", t: 2.6, h: 20, anim: { p: 30, dur: 7 },
    draw(p, t, o) { const mid = 15, r = o.p / 2; let d = `M ${f(o.x0)} ${mid}`;
      for (let x = o.x0; x < o.x1; x += o.p) d += ` A ${r} ${r} 0 0 1 ${f(x + o.p)} ${mid}`;
      return `<path d="${d}" fill="none" stroke="${p}" stroke-width="${t}"/>`; } },
  { fam: "waves", id: "ocean-curl", name: "Ocean wave", t: 2.6, h: 24, asym: true, anim: { p: 70, dur: 11 },
    draw(p, t, o) { const mid = 18, H = 13; let s = "";
      for (let x = o.x0; x < o.x1; x += o.p) { const d = `M ${f(x)} ${mid} L ${f(x + o.p * 0.1)} ${mid}`
          + ` Q ${f(x + o.p * 0.26)} ${f(mid - H)}, ${f(x + o.p * 0.46)} ${f(mid - H * 0.92)}`
          + ` Q ${f(x + o.p * 0.6)} ${f(mid - H * 0.85)}, ${f(x + o.p * 0.58)} ${f(mid - H * 0.35)}`
          + ` Q ${f(x + o.p * 0.57)} ${f(mid - 2)}, ${f(x + o.p * 0.7)} ${mid} L ${f(x + o.p)} ${mid}`;
        s += `<path d="${d}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linecap="round" stroke-linejoin="round"/>`;
        s += `<circle cx="${f(x + o.p * 0.5)}" cy="${f(mid - H * 0.55)}" r="1.9" fill="${p}"/>`; } return s; } },
  { fam: "waves", id: "swell", name: "Layered swell", t: 2.6, h: 24, lines: 2, asym: true, anim: { p: 64, dur: 10 },
    draw(pA, pB, t, o) { const back = `<path d="${sineP(9, 9, 64, o.x0, o.x1)}" fill="none" stroke="${pB}" stroke-width="${f(t * 0.8)}" stroke-linecap="round"/>`;
      const front = `<path d="${sineP(14, 13, 64, o.x0, o.x1)}" fill="none" stroke="${pA}" stroke-width="${t}" stroke-linecap="round"/>`;
      return back + front; } },
  { fam: "waves", id: "zigzag", name: "Zigzag", t: 2.6, h: 28, asym: true, anim: { p: 28, dur: 6 },
    draw(p, t, o) { const mid = 14, amp = 7, step = o.p / 2; let pts = [], i = 0;
      for (let x = o.x0; x <= o.x1; x += step) { pts.push(`${f(x)} ${f(mid + (i % 2 ? amp : -amp))}`); i++; }
      return `<path d="M ${pts.join(" L ")}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linejoin="round" stroke-linecap="round"/>`; } },
  { fam: "waves", id: "serpentine", name: "Serpentine scroll", t: 2, h: 26, asym: true, anim: { p: 40, dur: 9 },
    draw(p, t, o) { const mid = 13, amp = 8;
      let s = `<path d="${sineP(mid, amp * 2, o.p, o.x0, o.x1)}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linecap="round"/>`;
      for (let x = o.x0 + o.p / 4; x < o.x1; x += o.p) s += `<circle cx="${f(x)}" cy="${f(mid - amp * 0.55)}" r="2.4" fill="none" stroke="${p}" stroke-width="${f(t * 0.9)}"/>`;
      return s; } },
  /* Family 3 · Geometric & deco */
  { fam: "geo", id: "greek", name: "Greek key (meander)", t: 2, h: 24, asym: true, anim: { p: 30, dur: 11 },
    draw(p, t, o) { const u = 2.2, base = 22; let s = `<line x1="${f(o.x0)}" y1="${base}" x2="${f(o.x1)}" y2="${base}" stroke="${p}" stroke-width="${t}"/>`;
      for (let x = o.x0; x < o.x1; x += o.p) { const X = x + 4, B = base;
        const d = `M ${f(X)} ${f(B)} L ${f(X)} ${f(B - 8 * u)} L ${f(X + 8 * u)} ${f(B - 8 * u)} L ${f(X + 8 * u)} ${f(B)} `
               + `L ${f(X + 2 * u)} ${f(B)} L ${f(X + 2 * u)} ${f(B - 6 * u)} L ${f(X + 6 * u)} ${f(B - 6 * u)} L ${f(X + 6 * u)} ${f(B - 2 * u)} L ${f(X + 4 * u)} ${f(B - 2 * u)} L ${f(X + 4 * u)} ${f(B - 4 * u)}`;
        s += `<path d="${d}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linejoin="miter" stroke-linecap="square"/>`; }
      return s; } },
  { fam: "geo", id: "chain", name: "Chain link", t: 2.2, h: 22, anim: { p: 44, dur: 10 },
    draw(p, t, o) { const mid = 11; let s = "";
      for (let x = o.x0; x < o.x1; x += o.p) {
        s += `<rect x="${f(x)}" y="${f(mid - 6)}" width="50" height="12" rx="6" fill="none" stroke="${p}" stroke-width="${t}"/>`;
        s += `<rect x="${f(x + o.p / 2 - 6)}" y="${f(mid - 9)}" width="12" height="18" rx="6" fill="none" stroke="${p}" stroke-width="${t}"/>`; }
      return s; } },
  { fam: "geo", id: "chevron", name: "Chevron row", t: 2.6, h: 20, asym: true, anim: { p: 24, dur: 6 },
    draw(p, t, o) { const mid = 10, a = 6; let s = "";
      for (let x = o.x0; x < o.x1; x += o.p) { s += `<path d="M ${f(x)} ${f(mid - a)} L ${f(x + o.p * 0.6)} ${mid} L ${f(x)} ${f(mid + a)}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linejoin="round" stroke-linecap="round"/>`; }
      return s; } },
  { fam: "geo", id: "triangles", name: "Triangle teeth", t: 1, h: 18, anim: { p: 24, dur: 6 },
    draw(p, t, o) { const base = 15, H = 11; let s = "";
      for (let x = o.x0; x < o.x1; x += o.p) { s += `<path d="M ${f(x)} ${base} L ${f(x + o.p * 0.5)} ${f(base - H)} L ${f(x + o.p)} ${base} Z" fill="${p}"/>`; }
      return s; } },
  { fam: "geo", id: "hatch", name: "Diagonal hatch", t: 3, h: 18, asym: true, anim: { p: 14, dur: 5 },
    draw(p, t, o) { const top = 2, bot = 16; let s = "";
      for (let x = o.x0; x < o.x1; x += o.p) s += `<line x1="${f(x)}" y1="${bot}" x2="${f(x + 10)}" y2="${top}" stroke="${p}" stroke-width="${t}" stroke-linecap="round"/>`;
      return s; } },
  { fam: "geo", id: "lattice", name: "Woven lattice", t: 1.8, h: 22, anim: { p: 36, dur: 9 },
    draw(p, t, o) { const mid = 11, top = 3, bot = 19; let s = "";
      for (let x = o.x0; x < o.x1; x += o.p) {
        s += `<path d="M ${f(x)} ${mid} Q ${f(x + o.p / 4)} ${top}, ${f(x + o.p / 2)} ${mid} Q ${f(x + 3 * o.p / 4)} ${bot}, ${f(x + o.p)} ${mid}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linecap="round"/>`;
        s += `<path d="M ${f(x)} ${mid} Q ${f(x + o.p / 4)} ${bot}, ${f(x + o.p / 2)} ${mid} Q ${f(x + 3 * o.p / 4)} ${top}, ${f(x + o.p)} ${mid}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linecap="round"/>`; }
      return s; } },
  { fam: "geo", id: "deco-stepped", name: "Art-deco stepped", t: 1.6, h: 26,
    draw(p, t) { const mid = 13, cx = W / 2, gap = 34, step = 8; let s = "";
      s += `<path d="M 0 ${mid - 4} L ${cx - gap - step} ${mid - 4} L ${cx - gap} ${mid} L ${cx - 15} ${mid}" fill="none" stroke="${p}" stroke-width="${t}"/>`;
      s += `<path d="M ${W} ${mid - 4} L ${cx + gap + step} ${mid - 4} L ${cx + gap} ${mid} L ${cx + 15} ${mid}" fill="none" stroke="${p}" stroke-width="${t}"/>`;
      const hex = (r) => { let pts = []; for (let k = 0; k < 6; k++) { const ang = Math.PI / 180 * (60 * k); pts.push(`${f(cx + r * Math.cos(ang))} ${f(mid + r * 0.72 * Math.sin(ang))}`); } return pts.join(" L "); };
      s += `<path d="M ${hex(12)} Z" fill="none" stroke="${p}" stroke-width="${t}"/>`;
      s += `<circle cx="${cx}" cy="${mid}" r="2.2" fill="${p}"/>`; return s; } },
  { fam: "geo", id: "deco-center", name: "Art-deco diamonds", t: 1.5, h: 26,
    draw(p, t) { const mid = 13, cx = W / 2, gap = 30;
      let o = `<line x1="0" y1="${mid}" x2="${cx - gap}" y2="${mid}" stroke="${p}" stroke-width="${t}"/><line x1="${cx + gap}" y1="${mid}" x2="${W}" y2="${mid}" stroke="${p}" stroke-width="${t}"/>`;
      const dia = (s, fill) => `<path d="M ${cx} ${mid - s} L ${cx + s} ${mid} L ${cx} ${mid + s} L ${cx - s} ${mid} Z" fill="${fill ? p : "none"}" stroke="${p}" stroke-width="1.3"/>`;
      o += dia(11, false) + dia(6, false) + `<path d="M ${cx} ${mid - 2.4} L ${cx + 2.4} ${mid} L ${cx} ${mid + 2.4} L ${cx - 2.4} ${mid} Z" fill="${p}"/>`;
      for (const dx of [-20, 20]) { o += `<line x1="${cx + dx}" y1="${mid - 6}" x2="${cx + dx}" y2="${mid + 6}" stroke="${p}" stroke-width="1.3"/>`; }
      return o; } },
  /* Family 4 · Botanical & organic */
  { fam: "botanic", id: "leaf-vine", name: "Leaf vine", t: 1.6, h: 24, anim: { p: 42, dur: 10 },
    draw(p, t, o) { const mid = 12; let s = `<line x1="${f(o.x0)}" y1="${mid}" x2="${f(o.x1)}" y2="${mid}" stroke="${p}" stroke-width="${t}"/>`;
      const leaf = (x, dir) => `<path d="M ${f(x)} ${mid} C ${f(x - 3)} ${f(mid + dir * 3)}, ${f(x - 1)} ${f(mid + dir * 9)}, ${f(x + 3)} ${f(mid + dir * 10)} C ${f(x + 5)} ${f(mid + dir * 7)}, ${f(x + 4)} ${f(mid + dir * 2)}, ${f(x)} ${mid} Z" fill="${p}"/>`;
      let i = 0; for (let x = o.x0; x < o.x1; x += o.p / 2) { s += leaf(x, i % 2 ? 1 : -1); i++; } return s; } },
  { fam: "botanic", id: "laurel", name: "Laurel sprigs", t: 1.6, h: 26,
    draw(p, t) { const mid = 13, cx = W / 2, gap = 12, reach = 150; let s = "";
      const leaf = (x, dir, ud) => `<path d="M ${f(x)} ${mid} C ${f(x + dir * 2)} ${f(mid + ud * 4)}, ${f(x + dir * 3)} ${f(mid + ud * 9)}, ${f(x + dir * 9)} ${f(mid + ud * 9.5)} C ${f(x + dir * 7.5)} ${f(mid + ud * 5)}, ${f(x + dir * 6)} ${f(mid + ud * 2)}, ${f(x)} ${mid} Z" fill="${p}"/>`;
      for (const dir of [-1, 1]) {
        s += `<line x1="${f(cx + dir * gap)}" y1="${mid}" x2="${f(cx + dir * (gap + reach))}" y2="${mid}" stroke="${p}" stroke-width="${t}"/>`;
        for (let d = 16; d <= reach - 6; d += 18) { const x = cx + dir * (gap + d); s += leaf(x, dir, -1) + leaf(x, dir, 1); } }
      s += `<path d="M ${cx} ${mid - 5} L ${cx + 3.5} ${mid} L ${cx} ${mid + 5} L ${cx - 3.5} ${mid} Z" fill="${p}"/>`; return s; } },
  { fam: "botanic", id: "wheat", name: "Wheat spray", t: 1.6, h: 24, asym: true, anim: { p: 26, dur: 8 },
    draw(p, t, o) { const mid = 12; let s = `<line x1="${f(o.x0)}" y1="${mid}" x2="${f(o.x1)}" y2="${mid}" stroke="${p}" stroke-width="${t}"/>`;
      for (let x = o.x0; x < o.x1; x += o.p) { s += `<path d="M ${f(x)} ${mid} Q ${f(x + 6)} ${f(mid - 8)}, ${f(x + 13)} ${f(mid - 9)}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linecap="round"/>`;
        s += `<path d="M ${f(x)} ${mid} Q ${f(x + 6)} ${f(mid + 8)}, ${f(x + 13)} ${f(mid + 9)}" fill="none" stroke="${p}" stroke-width="${t}" stroke-linecap="round"/>`; } return s; } },
  { fam: "botanic", id: "berry", name: "Berry branch", t: 1.5, h: 22, anim: { p: 60, dur: 11 },
    draw(p, t, o) { const mid = 11; let s = `<line x1="${f(o.x0)}" y1="${mid}" x2="${f(o.x1)}" y2="${mid}" stroke="${p}" stroke-width="${t}"/>`;
      let i = 0; for (let x = o.x0; x < o.x1; x += o.p / 2) { const dir = i % 2 ? 1 : -1;
        s += `<path d="M ${f(x)} ${mid} Q ${f(x + 4)} ${f(mid + dir * 6)}, ${f(x + 10)} ${f(mid + dir * 5)} Q ${f(x + 5)} ${f(mid + dir * 2)}, ${f(x)} ${mid} Z" fill="${p}"/>`;
        s += `<circle cx="${f(x + o.p / 4)}" cy="${mid}" r="2.2" fill="${p}"/>`; i++; } return s; } },
  { fam: "botanic", id: "brush", name: "Brush stroke", t: 3, h: 18, asym: true,
    draw(p) { const d = `M 4 9 C 60 6.2, 130 7.0, 200 6.4 S 340 7.4, 410 6.6 S 520 7.8, 556 9 C 520 11.2, 410 10.2, 340 11.0 S 200 10.6, 130 11.4 S 60 10.6, 4 9 Z`;
      return `<path d="${d}" fill="${p}"/>`; } },
  /* Family 5 · Ornamental & center-motif (static) */
  { fam: "ornament", id: "scroll", name: "Victorian scroll", t: 1.4, h: 24,
    draw(p) { const mid = 12, cx = W / 2, gap = 48;
      const l = `<line x1="0" y1="${mid}" x2="${cx - gap}" y2="${mid}" stroke="${p}" stroke-width="1.4"/><line x1="${cx + gap}" y1="${mid}" x2="${W}" y2="${mid}" stroke="${p}" stroke-width="1.4"/>`;
      const fL = `<path d="M ${cx - gap} ${mid} C ${cx - 30} ${mid}, ${cx - 26} ${mid - 8}, ${cx - 16} ${mid - 7} C ${cx - 8} ${mid - 6}, ${cx - 8} ${mid}, ${cx - 14} ${mid + 2}" fill="none" stroke="${p}" stroke-width="1.4" stroke-linecap="round"/>`;
      const fR = `<path d="M ${cx + gap} ${mid} C ${cx + 30} ${mid}, ${cx + 26} ${mid - 8}, ${cx + 16} ${mid - 7} C ${cx + 8} ${mid - 6}, ${cx + 8} ${mid}, ${cx + 14} ${mid + 2}" fill="none" stroke="${p}" stroke-width="1.4" stroke-linecap="round"/>`;
      return l + fL + fR + `<circle cx="${cx}" cy="${mid}" r="2.4" fill="${p}"/>`; } },
  { fam: "ornament", id: "filigree", name: "Filigree flourish", t: 1.3, h: 24,
    draw(p) { const mid = 12, cx = W / 2, gap = 54;
      let s = `<line x1="0" y1="${mid}" x2="${cx - gap}" y2="${mid}" stroke="${p}" stroke-width="1.3"/><line x1="${cx + gap}" y1="${mid}" x2="${W}" y2="${mid}" stroke="${p}" stroke-width="1.3"/>`;
      const swash = (dir) => `<path d="M ${cx} ${mid} C ${f(cx + dir * 10)} ${mid - 9}, ${f(cx + dir * 34)} ${mid - 9}, ${f(cx + dir * 40)} ${mid} C ${f(cx + dir * 44)} ${mid + 7}, ${f(cx + dir * 28)} ${mid + 9}, ${f(cx + dir * 24)} ${mid + 3} C ${f(cx + dir * 21)} ${mid - 1}, ${f(cx + dir * 26)} ${mid - 5}, ${f(cx + dir * 30)} ${mid - 4}" fill="none" stroke="${p}" stroke-width="1.3" stroke-linecap="round"/>`;
      s += swash(-1) + swash(1) + `<circle cx="${cx}" cy="${mid}" r="2.4" fill="${p}"/>`; return s; } },
  { fam: "ornament", id: "double-diamond", name: "Rule + center diamond", t: 1.6, h: 24, lines: 2,
    draw(pA, pB, t, opt) { const mid = 12, s = 8, cx = W / 2, bg = opt.bg;
      const lines = `<line x1="0" y1="${mid - 5}" x2="${W}" y2="${mid - 5}" stroke="${pA}" stroke-width="1.6"/>`
                 + `<line x1="0" y1="${mid + 5}" x2="${W}" y2="${mid + 5}" stroke="${pB}" stroke-width="1.6"/>`;
      const halo = `<path d="M ${cx} ${mid - s - 3} L ${cx + s + 3} ${mid} L ${cx} ${mid + s + 3} L ${cx - s - 3} ${mid} Z" fill="${bg}"/>`;
      const dia = `<path d="M ${cx} ${mid - s} L ${cx + s} ${mid} L ${cx} ${mid + s} L ${cx - s} ${mid} Z" fill="${pA}"/>`;
      return lines + halo + dia; } },
  { fam: "ornament", id: "tapered", name: "Tapered swash rule", t: 4.4, h: 16,
    draw(p, t) { const mid = 8, T = Math.max(t * 2.2, 8);
      return `<path d="M 2 ${mid} Q ${W / 2} ${mid - T / 2}, ${W - 2} ${mid} Q ${W / 2} ${mid + T / 2}, 2 ${mid} Z" fill="${p}"/>`; } },
  { fam: "ornament", id: "short-centered", name: "Short centered rule", t: 3, h: 14,
    draw(p, t) { const mid = 7, bw = 150, bx = (W - bw) / 2, H = t, dr = Math.max(t / 1.5, 2);
      return `<rect x="${bx}" y="${f(mid - H / 2)}" width="${bw}" height="${H}" rx="${f(H / 2)}" fill="${p}"/>`
           + `<circle cx="${bx - 16}" cy="${mid}" r="${dr}" fill="${p}"/><circle cx="${bx + bw + 16}" cy="${mid}" r="${dr}" fill="${p}"/>`; } },
  { fam: "ornament", id: "arrow-rule", name: "Arrow rule", t: 1.8, h: 16,
    draw(p, t) { const mid = 8, cx = W / 2;
      let s = `<line x1="12" y1="${mid}" x2="${cx - 10}" y2="${mid}" stroke="${p}" stroke-width="${t}"/><line x1="${cx + 10}" y1="${mid}" x2="${W - 12}" y2="${mid}" stroke="${p}" stroke-width="${t}"/>`;
      s += `<path d="M 2 ${mid} L 13 ${mid - 4} L 13 ${mid + 4} Z" fill="${p}"/>`;
      s += `<path d="M ${W - 2} ${mid} L ${W - 13} ${mid - 4} L ${W - 13} ${mid + 4} Z" fill="${p}"/>`;
      s += `<path d="M ${cx} ${mid - 5} L ${cx + 5} ${mid} L ${cx} ${mid + 5} L ${cx - 5} ${mid} Z" fill="${p}"/>`; return s; } },
  { fam: "ornament", id: "fleuron", name: "Fleuron (bloom)", t: 1.4, h: 26,
    draw(p, t) { const mid = 13, cx = W / 2, gap = 26; let s = `<line x1="0" y1="${mid}" x2="${cx - gap}" y2="${mid}" stroke="${p}" stroke-width="1.4"/><line x1="${cx + gap}" y1="${mid}" x2="${W}" y2="${mid}" stroke="${p}" stroke-width="1.4"/>`;
      const petals = 8; for (let k = 0; k < petals; k++) { const a = Math.PI * 2 * k / petals; const x2 = cx + Math.cos(a) * 10, y2 = mid + Math.sin(a) * 10;
        s += `<path d="M ${cx} ${mid} Q ${f(cx + Math.cos(a - 0.28) * 7)} ${f(mid + Math.sin(a - 0.28) * 7)}, ${f(x2)} ${f(y2)} Q ${f(cx + Math.cos(a + 0.28) * 7)} ${f(mid + Math.sin(a + 0.28) * 7)}, ${cx} ${mid} Z" fill="${p}"/>`; }
      s += `<circle cx="${cx}" cy="${mid}" r="2.4" fill="${p}"/>`; return s; } },
  /* Family 6 · Multi-line & special */
  { fam: "special", id: "double-rule", name: "Double rule (two-tone)", t: 1.8, h: 16, lines: 2,
    draw(pA, pB, t) { const mid = 8;
      return `<rect x="0" y="${mid - 4}" width="${W}" height="${t}" rx="${t / 2}" fill="${pA}"/>`
           + `<rect x="0" y="${mid + 4 - t}" width="${W}" height="${t}" rx="${t / 2}" fill="${pB}"/>`; } },
  { fam: "special", id: "gradient-fade", name: "Gradient-fade edges", t: 3, h: 12, fade: true,
    draw(p, t) { const mid = 6, H = t; return `<rect x="0" y="${f(mid - H / 2)}" width="${W}" height="${H}" rx="${f(H / 2)}" fill="${p}"/>`; } },
  { fam: "special", id: "helix", name: "Double helix (DNA)", t: 2.4, h: 22, lines: 2, asym: true, anim: { p: 60, dur: 8 },
    draw(pA, pB, t, o) { return helixInner(pA, pB, t, 11, 12, 60, o.x0, o.x1); } },
];

/* the current 4, re-expressed as SVG so they render through the same path */
const CURRENT = [
  { fam: "basic", id: "solid", name: "Solid", t: 3, h: 10, draw(p, t) { return `<line x1="0" y1="5" x2="${W}" y2="5" stroke="${p}" stroke-width="${t}"/>`; } },
  { fam: "basic", id: "dashed", name: "Dashed", t: 3, h: 10, anim: { p: 24, dur: 7 }, draw(p, t, o) { return `<line x1="${f(o ? o.x0 : 0)}" y1="5" x2="${f(o ? o.x1 : W)}" y2="5" stroke="${p}" stroke-width="${t}" stroke-dasharray="14 10"/>`; } },
  { fam: "basic", id: "dotted-hair", name: "Dotted (hairline)", t: 3, h: 10, anim: { p: 12, dur: 6 }, draw(p, t, o) { return `<line x1="${f(o ? o.x0 : 0)}" y1="5" x2="${f(o ? o.x1 : W)}" y2="5" stroke="${p}" stroke-width="${t}" stroke-linecap="round" stroke-dasharray="0.1 12"/>`; } },
  { fam: "basic", id: "double", name: "Double", t: 1.6, h: 12, draw(p) { return `<rect x="0" y="3" width="${W}" height="1.6" fill="${p}"/><rect x="0" y="7.4" width="${W}" height="1.6" fill="${p}"/>`; } },
];

export const DIVIDER_FAMILIES = [
  { key: "basic", label: "Basic lines" },
  { key: "dots", label: "Dots & beads" },
  { key: "waves", label: "Waves & curves" },
  { key: "geo", label: "Geometric & deco" },
  { key: "botanic", label: "Botanical & organic" },
  { key: "ornament", label: "Ornamental & center" },
  { key: "special", label: "Multi-line & special" },
];

/** All 37 styles (4 current-as-SVG + 33 new), in family order. */
export const DIVIDER_STYLES = CURRENT.concat(STYLES);

const BY_ID = DIVIDER_STYLES.reduce((m, s) => {
  m[s.id] = s;
  return m;
}, {});

/** Look up a style descriptor by id (undefined if unknown). */
export function getDividerStyle(id) {
  return BY_ID[id];
}

/** True if `id` is a known SVG divider style. */
export function isSvgDividerStyle(id) {
  return typeof id === "string" && Object.prototype.hasOwnProperty.call(BY_ID, id);
}

/** Descriptors grouped by family, in DIVIDER_FAMILIES order (for the picker). */
export function dividerStylesByFamily() {
  return DIVIDER_FAMILIES.map((fam) => ({
    ...fam,
    items: DIVIDER_STYLES.filter((s) => s.fam === fam.key),
    animCount: DIVIDER_STYLES.filter((s) => s.fam === fam.key && s.anim).length,
  }));
}

/** True if the style supports opt-in drift animation. */
export function isAnimatable(id) {
  const s = BY_ID[id];
  return !!(s && s.anim);
}

/** True if the style is asymmetric (can be flipped). */
export function isFlippable(id) {
  const s = BY_ID[id];
  return !!(s && s.asym);
}

/** True if the style paints two independently-colorable lines. */
export function isMultiLine(id) {
  const s = BY_ID[id];
  return !!(s && (s.lines || 1) >= 2);
}

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/**
 * Build the inline SVG string for a divider style.
 *
 * @param {string} id            style id (e.g. "wavy"); unknown -> "" (caller falls back)
 * @param {object} opts
 * @param {string} opts.color    primary line color (any CSS color; sanitized)
 * @param {string} [opts.colorB] secondary color for multi-line styles (default = color)
 * @param {number} [opts.thickness=3] block Thickness; scales each style's tuned weight
 *                                     relative to the design default of 3
 * @param {boolean}[opts.animate=false] opt-in drift (only applies to animatable styles)
 * @param {string} [opts.direction="left"] drift direction: "left" | "right"
 * @param {number} [opts.zoom]   center-crop factor for a compact picker thumbnail
 * @param {string} [opts.uid]    unique id seed for gradient defs (avoid DOM id clashes)
 * @returns {string} the `<svg>...</svg>` markup, or "" if the id is unknown
 */
export function buildDividerSVG(id, opts = {}) {
  const style = BY_ID[id];
  if (!style) return "";

  const color = sanitizeColor(opts.color) || "#000000";
  const colorB = sanitizeColor(opts.colorB) || color;
  const thickness = Number.isFinite(opts.thickness) ? opts.thickness : 3;
  // scale each style's tuned base weight proportionally (default 3 -> x1 = exact deck look)
  const t = clamp((style.t || 3) * (thickness / 3), 0.4, (style.t || 3) * 8);

  const anim = !!opts.animate && !!style.anim;
  const P = style.anim ? style.anim.p : 0;
  const dir = opts.direction === "right" ? "right" : "left";
  const opt = { bg: "#FFFFFF", dir, anim, p: P, x0: anim ? -P : 0, x1: anim ? W + P : W };
  const uid = "dvg_" + (opts.uid || style.id);

  let defs = "";
  let inner;
  if (style.fade) {
    // edge-fade of the user's own color (userSpaceOnUse keeps the zero-height line visible)
    const gid = uid + "_fade";
    defs =
      `<linearGradient id="${gid}" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${W}" y2="0">` +
      `<stop offset="0%" stop-color="${color}" stop-opacity="0"/>` +
      `<stop offset="18%" stop-color="${color}" stop-opacity="1"/>` +
      `<stop offset="82%" stop-color="${color}" stop-opacity="1"/>` +
      `<stop offset="100%" stop-color="${color}" stop-opacity="0"/></linearGradient>`;
    inner = style.draw(`url(#${gid})`, t, opt);
  } else if ((style.lines || 1) >= 2) {
    inner = style.draw(color, colorB, t, opt);
  } else {
    inner = style.draw(color, t, opt);
  }

  const gd = defs ? `<defs>${defs}</defs>` : "";
  const core = anim
    ? `<g class="dv-drift${dir === "right" ? " rev" : ""}" style="--dx:-${P}px;--dur:${style.anim.dur}s">${inner}</g>`
    : inner;
  return wrap(style.h, gd + core, opts.zoom);
}
